// ---------------------------------------------------------------------------
// Authorization & ABAC Types
// ---------------------------------------------------------------------------

/** System-level roles (non-attribute, coarse-grained tiers) */
export type SystemRole =
  | 'GlobalAdmin'       // Platform master dev — all orgs, internal-surface only (role_model_globaladmin_orgadmin)
  | 'OrgAdmin'          // Customer top admin — full admin within ONE org (org-scoping enforced by fork RLS)
  | 'GlobalViewer'      // Read-only across all contexts
  | 'ConfigAdmin'       // Configuration context only
  /** Manufacturing-time supervisor (TS-1.5). Can view direct-report workers' days + floor activity. */
  | 'Lead'
  /** Timekeeper (TS-1.5). Can edit time entries within compliance window + run floor checks. */
  | 'Timekeeper'
  /** Compliance officer (TS-1.5). Read-only access to all time records + audit trail; can lock periods. */
  | 'ComplianceOfficer'
  /** Auditor (TS-1.5). Strict read-only access for government / external audit walkthrough. */
  | 'Auditor'
  | 'None'              // No system role — access determined by portfolio assignments

/**
 * System roles with full admin capability. GlobalAdmin (platform master dev)
 * and OrgAdmin (customer top admin) get the SAME in-app admin powers; they
 * differ only in org reach — GlobalAdmin spans all orgs (internal surface),
 * OrgAdmin is scoped to its org by RLS in a customer fork. Use this wherever an
 * "is this user an admin?" gate previously checked only `=== 'GlobalAdmin'`.
 */
export function isAdminSystemRole(role: string | null | undefined): boolean {
  return role === 'GlobalAdmin' || role === 'OrgAdmin'
}

/** Portfolio-scoped roles (attribute-based, fine-grained) */
export type PortfolioRole =
  | 'PortfolioManager'  // Full view/edit on assigned portfolios
  | 'PortfolioAnalyst'  // View all, edit budgets/forecasts/analysis
  | 'ProjectManager'    // View/edit assigned projects within portfolio
  | 'ContractsAdmin'    // Full access to contracts in assigned portfolios
  | 'FinanceManager'    // Financial data access in assigned portfolios
  | 'Viewer'            // Read-only within assigned portfolios

/** All possible roles */
export type UserRole = SystemRole | PortfolioRole

/** Actions that can be performed on resources */
export type Action = 'view' | 'edit' | 'create' | 'delete' | 'approve' | 'manage'

/** Resource types in the system */
export type ResourceType =
  | 'Portfolio'
  | 'Project'
  | 'Risk'
  | 'WBS'
  | 'Task'
  | 'Budget'
  | 'BudgetData'
  | 'Contract'
  | 'EVM'
  | 'EVMData'
  | 'Forecast'
  | 'Resource'
  | 'Workflow'
  | 'Report'
  | 'Analysis'
  | 'Scenario'
  | 'ProjectRequest'
  | 'AbacPolicy'
  | 'RoleDefinition'
  | 'UserAssignment'
  | 'AuditLog'
  | 'Notification'
  | 'Schedule'
  | 'Baseline'
  | 'TimeEntry'
  | 'TimesheetSubmission'
  | 'TimesheetApproval'
  | 'TimesheetReport'
  | 'ChargeCode'
  | 'CostTransfer'

// ---------------------------------------------------------------------------
// ABAC Policy Types (matches mock data shape)
// ---------------------------------------------------------------------------

export type PolicyEffect = 'Permit' | 'Deny'

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'in'
  | 'not_in'
  | 'greater_than'
  | 'less_than'

export interface PolicySubjectMatch {
  attributeName: string
  attributeValue: string
  operator: ConditionOperator
}

export interface PolicyResourceMatch {
  attributeName: string
  attributeValue: string
  operator: ConditionOperator
}

export interface PolicyActionMatch {
  actionId: string
  actionName: string
}

export interface PolicyEnvironmentMatch {
  attributeName: string
  attributeValue: string
  operator: ConditionOperator
}

export interface PolicyCondition {
  conditionId: string
  conditionName: string
  expression: string
}

export interface PolicyObligation {
  id: string
  name: string
}

export interface PolicyTarget {
  subjects: PolicySubjectMatch[]
  resources: PolicyResourceMatch[]
  actions: PolicyActionMatch[]
  environment: PolicyEnvironmentMatch[]
}

export interface AbacPolicy {
  policyId: string
  policyName: string
  description: string
  effect: PolicyEffect
  priority: number
  active: boolean
  target: PolicyTarget
  conditions: PolicyCondition[]
  obligations: PolicyObligation[]
}

// ---------------------------------------------------------------------------
// Role Definitions
// ---------------------------------------------------------------------------

export interface RoleDefinition {
  id: string
  name: string
  description: string
  permissions: string[]
  systemRole?: boolean
}

// ---------------------------------------------------------------------------
// UI Policy Mappings
// ---------------------------------------------------------------------------

export interface UiPolicyMapping {
  componentId: string
  requiredAction: Action
  resourceType: ResourceType
  defaultVisible: boolean
  defaultEnabled: boolean
}

// ---------------------------------------------------------------------------
// Portfolio Assignment
// ---------------------------------------------------------------------------

export interface PortfolioAssignment {
  userId: string
  userName: string
  portfolioId: string
  portfolioName: string
  role: PortfolioRole
  assignedDate: string
  assignedBy: string
}

// ---------------------------------------------------------------------------
// Extended User (with authorization attributes)
// ---------------------------------------------------------------------------

// ProjectTasks #78f — account type + multi-organization affiliation.
// Locked design decisions (2026-05-14): demo gates BOTH features + data;
// Org SUPPLEMENTS Portfolio (additional filter, doesn't replace);
// terminology is 'production' (not 'productive'); access entries support
// per-entry includeDescendants for hierarchical org-tree inheritance.
export type AccountType = 'demo' | 'production'

export type OrganizationAccessLevel = 'view' | 'edit' | 'admin'

export interface OrganizationAccess {
  organizationId: string
  organizationName?: string
  accessLevel: OrganizationAccessLevel
  /** When true, access propagates to descendant orgs in the parent_id chain. */
  includeDescendants: boolean
  grantedBy?: string | null
  grantedAt?: string | null
  notes?: string | null
}

export interface AuthUser {
  id: string
  name: string
  email: string
  department: string
  title: string
  initials: string
  systemRole: SystemRole
  clearance: string
  costCenterCode: string
  portfolioAssignments: PortfolioAssignment[]
  // ProjectTasks #78f Slice 1 — account type + multi-organization affiliation.
  accountType: AccountType
  primaryOrganizationId: string | null
  organizationAccess: OrganizationAccess[]
}

// ---------------------------------------------------------------------------
// Authorization Request & Decision
// ---------------------------------------------------------------------------

/** The context for an authorization decision */
export interface AuthorizationRequest {
  subject: {
    userId: string
    role: UserRole
    systemRole: SystemRole
    assignedPortfolios: string[]
    clearance: string
    department: string
    // ProjectTasks #78f Slice 2 — org-scope + account-type attributes.
    // Populated by checkPermission() from the loaded AuthUser when not
    // passed explicitly. Optional so legacy callers still type-check.
    accountType?: AccountType
    primaryOrganizationId?: string | null
    /** All organization IDs the subject can access (primary + accessible).
     *  When the user has `includeDescendants` on any access entry, this
     *  list is pre-expanded by the org tree resolver. */
    accessibleOrganizationIds?: string[]
    [key: string]: unknown
  }
  action: Action
  resource: {
    type: ResourceType
    id?: string
    portfolioId?: string
    classification?: string
    // ProjectTasks #78f Slice 2 — org tag on the resource so the policy
    // engine can match org-scope. Pulled from the resource's own
    // organizationId / primary_organization_id when present.
    organizationId?: string
    /** 'demo' on resources tagged as demo-only (won't show to production
     *  accounts UNLESS they have the cross-mode view flag) or 'production'
     *  for the standard tenant data. */
    dataClass?: 'demo' | 'production'
    [key: string]: unknown
  }
  environment?: {
    time?: string
    ipAddress?: string
    [key: string]: unknown
  }
}

/** Result of a policy evaluation */
export interface AuthorizationDecision {
  allowed: boolean
  effect: PolicyEffect
  matchedPolicies: string[]
  obligations: PolicyObligation[]
  reason: string
}

// ---------------------------------------------------------------------------
// Navigation Permission Map
// ---------------------------------------------------------------------------

/** Maps sidebar nav keys to required permissions */
export interface NavPermission {
  key: string
  action: Action
  resourceType: ResourceType
  /** If true, requires portfolio assignment (not just system role) */
  requiresPortfolioAssignment?: boolean
}

// ---------------------------------------------------------------------------
// Audit Entry for Authorization Decisions
// ---------------------------------------------------------------------------

export interface AuthAuditEntry {
  id: string
  timestamp: string
  userId: string
  userName: string
  action: Action
  resourceType: ResourceType
  resourceId: string
  decision: 'Permit' | 'Deny'
  matchedPolicies: string[]
  reason: string
}

// ---------------------------------------------------------------------------
// Effective Permission (matched-action summary for a resource type)
// ---------------------------------------------------------------------------

export interface EffectivePermission {
  resourceType: ResourceType
  actions: { action: Action; allowed: boolean; reason: string }[]
}
